import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Button } from '@ui/button.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useDebounce } from '@r/hooks/use-debounce.ts';
import { type FC, useContext, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { isTypologyConversion } from '@r/utils/workspace-utils.ts';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { useQueryClient } from '@tanstack/react-query';
import {
  apiAdminusersGetCollection,
  useApiAdmincompaniesPost,
  useApiAdmincompaniesCompanyIdresponsiblePost,
  useApiCountriesGetCollection,
  useApiAdmintypologiesGetCollection,
  useApiAdminusersGetCollection,
  getApiAdmincompaniesIdGetQueryKey,
  getApiAdmincompaniesGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import type { UserUserCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { MultiSelect, type MultiSelectOption } from '@ui/multi-select.tsx';
import { roleToI18nKey, type Role } from '@r/utils/roles.ts';

interface CreateCompanyFormProps {
  onSuccess: () => void;
}

const COMMENT_MAX_LENGTH = 255;
const COMPANY_RESPONSIBLES_LIMIT = 10;
const USER_WORKSPACE_IRI_PREFIX = '/user_workspaces';

export const CreateCompanyForm: FC<CreateCompanyFormProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const navigate = useNavigate();
  const workspaceConfigContext = useContext(WorkspaceConfigContext);
  const queryClient = useQueryClient();

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 500);
  const [selectedResponsibleUniqueCodes, setSelectedResponsibleUniqueCodes] = useState<string[]>(
    []
  );

  const { data: countriesData, isLoading: isLoadingCountries } = useApiCountriesGetCollection();

  const { data: typologiesData, isLoading: isLoadingTypologies } =
    useApiAdmintypologiesGetCollection(undefined, {
      query: { enabled: isTypologyConversion(workspaceConfigContext) }
    });

  const responsibleQuery = useApiAdminusersGetCollection(
    {
      search: debouncedQuery,
      perPage: 100,
      'maxRole[]': ['ROLE_SALES_ADMIN']
    },
    { query: { enabled: true } }
  );

  const createCompanySchema = z.object({
    name: z
      .string()
      .min(1, t('company.create.name.required'))
      .max(100, t('company.create.name.max')),
    uniqueCode: z
      .string()
      .min(1, t('company.create.unique.code.required'))
      .max(50, t('company.create.unique.code.max'))
      .regex(/^([a-zA-Z0-9]+)$/, t('company.create.unique.code.regex'))
      .describe(t('form.fields.schema.describe.unique.code')),
    siren: z.string().optional().describe(t('form.fields.schema.describe.siren')),
    tva: z.string().optional().describe(t('form.fields.schema.describe.tva')),
    addressLine1: z
      .string()
      .min(1, t('company.create.address.line.1.required'))
      .max(255, t('company.create.address.line.1.max'))
      .describe(t('form.fields.schema.describe.address.line')),
    addressLine2: z.string().optional().describe(t('form.fields.schema.describe.address.line')),
    addressLine3: z.string().optional().describe(t('form.fields.schema.describe.address.line')),
    zipcode: z
      .string()
      .min(1, t('company.create.zipcode.required'))
      .max(20, t('company.create.zipcode.max'))
      .describe(t('form.fields.schema.describe.zipcode')),
    city: z
      .string()
      .min(1, t('company.create.city.required'))
      .max(100, t('company.create.city.max'))
      .describe(t('form.fields.schema.describe.city')),
    countryAlpha3: z.string().min(1, t('company.create.country.alpha.3.required')),
    responsibleUniqueCodes: z
      .array(z.string())
      .min(1, t('company.create.responsible.unique.code.required'))
      .max(COMPANY_RESPONSIBLES_LIMIT),
    typologyName: z.union([z.string(), z.null()]).optional(),
    pointCoeff: z
      .union([
        z.coerce
          .number({
            message: t('company.create.point.coeff.invalid'),
            invalid_type_error: t('company.create.point.coeff.invalid')
          })
          .min(0.001, t('company.create.point.coeff.min')),
        z.undefined()
      ])
      .describe(t('form.fields.schema.describe.point.coeff'))
      .refine((val) => {
        if (isTypologyConversion(workspaceConfigContext)) {
          return true;
        }
        return val !== undefined && val > 0;
      }, t('company.create.point.coeff.required'))
      .refine((val) => {
        if (val === undefined) return true;
        const decimalPlaces = val.toString().split('.')[1]?.length || 0;
        return decimalPlaces <= 3;
      }, t('company.create.point.coeff.decimal.max')),
    comment: z
      .string()
      .max(COMMENT_MAX_LENGTH, t('company.comment.max.length', { max: COMMENT_MAX_LENGTH }))
      .optional()
  });

  const form = useForm<z.infer<typeof createCompanySchema>>({
    resolver: zodResolver(createCompanySchema),
    defaultValues: {
      name: '',
      uniqueCode: '',
      siren: '',
      tva: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      zipcode: '',
      city: '',
      countryAlpha3: 'FRA',
      responsibleUniqueCodes: [],
      typologyName: '',
      pointCoeff: undefined,
      comment: ''
    }
  });

  const createCompanyMutation = useApiAdmincompaniesPost();
  const addResponsibleMutation = useApiAdmincompaniesCompanyIdresponsiblePost();

  const responsibleWorkspaceIdsByUniqueCode = new Map<string, number>();

  for (const responsible of responsibleQuery.data?.data ?? []) {
    const workspace = getActiveWorkspaceUniversal(responsible.userWorkspaces);

    if (workspace?.uniqueCode && workspace.id) {
      responsibleWorkspaceIdsByUniqueCode.set(workspace.uniqueCode, workspace.id);
    }
  }

  const responsibleOptions = (responsibleQuery.data?.data ?? [])
    .map((responsible: UserUserCollectionReadModel) => {
      const workspace = getActiveWorkspaceUniversal(responsible.userWorkspaces);

      return {
        label: responsible.fullname || `${responsible.firstname} ${responsible.lastname}`,
        value: workspace?.uniqueCode ?? '',
        description: workspace?.maxRole ? t(roleToI18nKey(workspace.maxRole as Role)) : undefined,
        disabled: !workspace?.id
      };
    })
    .reduce<MultiSelectOption[]>((options, option) => {
      if (
        !option.value ||
        options.some((existingOption) => existingOption.value === option.value)
      ) {
        return options;
      }

      options.push(option);
      return options;
    }, []);

  const getResponsibleWorkspaceId = async (uniqueCode: string): Promise<number | undefined> => {
    const knownResponsibleId = responsibleWorkspaceIdsByUniqueCode.get(uniqueCode);
    if (knownResponsibleId) return knownResponsibleId;

    const response = await apiAdminusersGetCollection({
      'userWorkspaces.uniqueCode': uniqueCode,
      'maxRole[]': ['ROLE_SALES_ADMIN'],
      perPage: 1
    });
    const users = response.data as UserUserCollectionReadModel[];
    const matchingWorkspace = users
      ?.flatMap((responsible) => responsible.userWorkspaces ?? [])
      .find((workspace) => workspace.uniqueCode === uniqueCode);

    return matchingWorkspace?.id;
  };

  async function onSubmit(data: z.infer<typeof createCompanySchema>) {
    try {
      const [firstResponsibleUniqueCode, ...nextResponsibleUniqueCodes] =
        data.responsibleUniqueCodes.slice(0, COMPANY_RESPONSIBLES_LIMIT);
      const company = await createCompanyMutation.mutateAsync({
        data: {
          name: data.name,
          uniqueCode: data.uniqueCode,
          siren: data.siren && data.siren.trim().length > 0 ? data.siren : null,
          tva: data.tva && data.tva.trim().length > 0 ? data.tva : null,
          addressLine1: data.addressLine1,
          addressLine2:
            data.addressLine2 && data.addressLine2.trim().length > 0 ? data.addressLine2 : null,
          addressLine3:
            data.addressLine3 && data.addressLine3.trim().length > 0 ? data.addressLine3 : null,
          zipcode: data.zipcode,
          city: data.city,
          countryAlpha3: data.countryAlpha3,
          responsiblesUniqueCodes: firstResponsibleUniqueCode,
          typologyName:
            data.typologyName && data.typologyName.trim().length > 0 ? data.typologyName : null,
          pointCoeff: data.pointCoeff,
          comment: data.comment && data.comment.trim().length > 0 ? data.comment.trim() : null
        }
      });

      await Promise.all(
        nextResponsibleUniqueCodes.map(async (uniqueCode) => {
          const responsibleId = await getResponsibleWorkspaceId(uniqueCode);
          if (!responsibleId) throw new Error(`Missing user workspace id for ${uniqueCode}`);

          return addResponsibleMutation.mutateAsync({
            companyId: company.id,
            data: { responsible: `${USER_WORKSPACE_IRI_PREFIX}/${responsibleId}` }
          });
        })
      );

      queryClient.setQueryData(getApiAdmincompaniesIdGetQueryKey(company.id?.toString()), company);
      queryClient.invalidateQueries({
        queryKey: getApiAdmincompaniesIdGetQueryKey(company.id.toString())
      });
      queryClient.invalidateQueries({ queryKey: getApiAdmincompaniesGetCollectionQueryKey() });
      toast.success(t('company.create.success'));
      form.reset();
      setSelectedResponsibleUniqueCodes([]);
      onSuccess();
      navigate({ to: `/companies/${company.id}` });
    } catch (error) {
      showOrvalErrorToast(error, t('company.create.error'));
    }
  }

  return (
    <Form {...form} schema={createCompanySchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel className="flex gap-2 items-center">
                {t('company.create.name.label')}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t('company.create.name.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="uniqueCode"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel className="flex gap-2 items-center">
                {t('company.create.unique.code.label')}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t('company.create.unique.code.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="siren"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('company.create.siren.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('company.create.siren.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="tva"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('company.create.tva.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('company.create.tva.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="addressLine1"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel className="flex gap-2 items-center">
                {t('company.create.address.line.1.label')}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t('company.create.address.line.1.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="addressLine2"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('company.create.address.line.2.label')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('company.create.address.line.2.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="addressLine3"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('company.create.address.line.3.label')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('company.create.address.line.3.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="zipcode"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="flex gap-2 items-center">
                  {t('company.create.zipcode.label')}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('company.create.zipcode.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="city"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="flex gap-2 items-center">
                  {t('company.create.city.label')}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('company.create.city.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="countryAlpha3"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex gap-2 items-center">
                {t('company.create.country.alpha.3.label')}
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('company.create.country.alpha.3.placeholder')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {isLoadingCountries ? (
                    <SelectItem value="loading" disabled>
                      {t('company.create.country.alpha.3.loading')}
                    </SelectItem>
                  ) : (
                    (
                      (countriesData?.data ?? []) as Array<{
                        alpha2: string;
                        alpha3: string;
                        name: string;
                      }>
                    ).map((country) => (
                      <SelectItem key={country.alpha3} value={country.alpha3}>
                        <div className="flex gap-2 items-center">{country.name}</div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {isTypologyConversion(workspaceConfigContext) ? (
          <FormField
            control={form.control}
            name="typologyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex gap-2 items-center">
                  {t('company.create.typology.name.label')}
                </FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value ?? undefined}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('company.create.typology.name.placeholder')} />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent>
                    {isLoadingTypologies ? (
                      <SelectItem value="loading" disabled>
                        {t('company.create.typology.name.loading')}
                      </SelectItem>
                    ) : (
                      (
                        (typologiesData?.data ?? []) as Array<{
                          id: number;
                          name: string;
                          pointCoeff: number;
                          isDefault: boolean;
                        }>
                      ).map((typology) => (
                        <SelectItem key={typology.id} value={typology.name}>
                          {typology.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : (
          <FormField
            control={form.control}
            name="pointCoeff"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('company.profile.point.coefficient')}</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder={t('company.profile.point.coefficient')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage className="mt-1 text-xs" />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="comment"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('label.company.comment')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('company.comment.placeholder')}
                  {...field}
                  value={field.value ?? ''}
                  maxLength={COMMENT_MAX_LENGTH}
                  rows={4}
                  className="resize-none"
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="responsibleUniqueCodes"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex gap-2 items-center">
                {t('company.create.responsible.unique.code.label')}
              </FormLabel>
              <MultiSelect
                placeholder={t('company.create.responsible.unique.code.placeholder')}
                searchPlaceholder={t('company.create.responsible.unique.code.placeholder')}
                options={responsibleOptions}
                value={selectedResponsibleUniqueCodes}
                onValueChange={(responsibleUniqueCodes) => {
                  const nextResponsibleUniqueCodes = responsibleUniqueCodes.slice(
                    0,
                    COMPANY_RESPONSIBLES_LIMIT
                  );
                  setSelectedResponsibleUniqueCodes(nextResponsibleUniqueCodes);
                  field.onChange(nextResponsibleUniqueCodes);
                }}
                searchValue={query}
                onSearchChange={setQuery}
                loading={responsibleQuery.isLoading}
                maxSelected={COMPANY_RESPONSIBLES_LIMIT}
                maxSelectedMessage={`Limite de ${COMPANY_RESPONSIBLES_LIMIT} responsables atteinte.`}
              />
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          loading={createCompanyMutation.isPending || addResponsibleMutation.isPending}
          className="w-full"
          type="submit"
        >
          <Plus className="mr-2 w-4 h-4" />
          {t('company.create.form.button')}
        </Button>
      </form>
    </Form>
  );
};
